import type { ItemId, Project, ScenarioId } from '@finsim/model';
import { attach, createEvent, sample } from 'effector';
import { toastShown } from '../../entities/notifications';
import { $project, commandIssued, undo, usageCount, type Command } from '../../entities/project';
import { itemSelected } from '../../entities/selection';
import { newId } from '../../shared/lib/id';

/*
 * Item actions shared by the tree context menu, the inspector and keyboard shortcuts.
 * Destructive ones confirm with an Undo toast rather than a dialog (Linear-style).
 */

interface Target {
  itemId: ItemId;
  scenarioId: ScenarioId;
}

export const duplicateRequested = createEvent<Target & { name: string }>();
/** Also use the item in another variant. */
export const shareRequested = createEvent<Target>();
export const detachRequested = createEvent<Target>();
/** Remove from this variant (deletes it when no other variant uses it). */
export const removeRequested = createEvent<Target>();
export const deleteEverywhereRequested = createEvent<{ itemId: ItemId }>();

const names = (project: Project, { itemId, scenarioId }: Partial<Target>) => ({
  name: (itemId && project.items[itemId]?.name) ?? '',
  variant: project.scenarios.find((s) => s.id === scenarioId)?.name ?? '',
});

const issue = (command: Command) => ({ command });

const duplicateFx = attach({
  source: $project,
  effect: (_, target: Target & { name: string }) => ({ ...target, newId: newId('itm') as ItemId }),
});
sample({ clock: duplicateRequested, target: duplicateFx });
sample({
  clock: duplicateFx.doneData,
  fn: ({ itemId, scenarioId, newId: id, name }) =>
    issue({ type: 'item/duplicate', id: itemId, scenarioId, newId: id, name }),
  target: commandIssued,
});
sample({
  clock: duplicateFx.doneData,
  fn: ({ newId: itemId, scenarioId }) => ({ itemId, scenarioId }),
  target: itemSelected,
});

sample({
  clock: shareRequested,
  fn: ({ itemId, scenarioId }) => issue({ type: 'item/share', id: itemId, scenarioId }),
  target: commandIssued,
});
sample({
  clock: shareRequested,
  source: $project,
  filter: Boolean,
  fn: (project, target) => ({
    message: 'itemActions.sharedInto' as const,
    params: names(project, target),
  }),
  target: toastShown,
});

const detachFx = attach({
  source: $project,
  effect: (project, target: Target) => {
    if (!project) throw new Error('No project open');
    return { ...target, newId: newId('itm') as ItemId, ...names(project, target) };
  },
});
sample({ clock: detachRequested, target: detachFx });
sample({
  clock: detachFx.doneData,
  fn: ({ itemId, scenarioId, newId: id }) =>
    issue({ type: 'item/detach', id: itemId, scenarioId, newId: id }),
  target: commandIssued,
});
sample({
  clock: detachFx.doneData,
  fn: ({ newId: itemId, scenarioId }) => ({ itemId, scenarioId }),
  target: itemSelected,
});
sample({
  clock: detachFx.doneData,
  fn: ({ name, variant }) => ({
    message: 'itemActions.detached' as const,
    params: { name, variant },
  }),
  target: toastShown,
});

const undoAction = { label: 'common.undo' as const, event: undo };

sample({
  clock: removeRequested,
  source: $project,
  filter: Boolean,
  fn: (project, target) => {
    const lastUse = (usageCount(project).get(target.itemId) ?? 0) <= 1;
    return {
      message: lastUse ? ('itemActions.deleted' as const) : ('itemActions.removed' as const),
      params: names(project, target),
      action: undoAction,
    };
  },
  target: toastShown,
});
sample({
  clock: removeRequested,
  fn: ({ itemId, scenarioId }) => issue({ type: 'item/remove', id: itemId, scenarioId }),
  target: commandIssued,
});

sample({
  clock: deleteEverywhereRequested,
  source: $project,
  filter: Boolean,
  fn: (project, { itemId }) => ({
    message: 'itemActions.deleted' as const,
    params: names(project, { itemId }),
    action: undoAction,
  }),
  target: toastShown,
});
sample({
  clock: deleteEverywhereRequested,
  fn: ({ itemId }) => issue({ type: 'item/remove', id: itemId, scenarioId: null }),
  target: commandIssued,
});

/** Copy an item into another variant as an independent item. */
export const copyToRequested = createEvent<Target>();

const copyToFx = attach({
  source: $project,
  effect: (project, target: Target) => {
    const item = project?.items[target.itemId];
    if (!project || !item) throw new Error('Unknown item');
    return {
      item: { ...item, id: newId('itm') as ItemId },
      scenarioId: target.scenarioId,
      ...names(project, target),
    };
  },
});
sample({ clock: copyToRequested, target: copyToFx });
sample({
  clock: copyToFx.doneData,
  fn: ({ item, scenarioId }) => issue({ type: 'item/add', item, scenarioId }),
  target: commandIssued,
});
sample({
  clock: copyToFx.doneData,
  fn: ({ name, variant }) => ({
    message: 'itemActions.copiedInto' as const,
    params: { name, variant },
  }),
  target: toastShown,
});
